"use client";

import type { CSSProperties, HTMLAttributes } from "react";
import { useInView } from "./Reveal";

interface RevealGroupProps extends HTMLAttributes<HTMLDivElement> {
  /** Per-child stagger step in ms. Brief default 90. */
  stagger?: number;
  threshold?: number;
  once?: boolean;
}

/**
 * SI-06 stagger-in group. Direct children fade and rise 24px with a
 * staggered delay once the wrapper is 15% visible. Default for card
 * grids, list rows and footer columns. The stagger is applied via
 * nth-child CSS in globals.css (capped at eleven steps), so children
 * need no wrapping or extra props:
 *
 *   <RevealGroup className="grid grid-cols-3 gap-8">
 *     <Card /> <Card /> <Card />
 *   </RevealGroup>
 */
export function RevealGroup({
  children,
  stagger = 90,
  threshold = 0.15,
  once = true,
  className = "",
  style,
  ...rest
}: RevealGroupProps) {
  const [ref, visible] = useInView<HTMLDivElement>({ threshold, once });

  const composedStyle: CSSProperties = {
    "--stagger-step": `${stagger}ms`,
    ...style,
  } as CSSProperties;

  return (
    <div
      ref={ref}
      data-reveal-group=""
      data-reveal-in={visible ? "true" : "false"}
      className={className}
      style={composedStyle}
      {...rest}
    >
      {children}
    </div>
  );
}
